
class ElectronicInvoicingEventEmitter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async getInvoicesWithPaymentEvents (origin, companyCode, startDate) {
    const {rows} = await this.pgConnection.query(`
      with payment_linked_invoices as (
        select
          p.origin,
          p.company,
          pl.linked_id as invoice_txn_id
        from fin.payment p
        inner join fin.payment_line pl on
          p.txn_id = pl.txn_id and
          p.company = pl.company and
          p.origin = pl.origin
        left join fin.txn t on t.txn_id = p.txn_id and t.company = p.company and t.origin = p.origin
        where pl.linked_type = 'invoice' and t.created_at >= $1 and p.company = $2 and p.origin = $3
        group by 1, 2, 3
      ),
      deposit_linked_loan_accounts as (
        select
          d.origin,
          d.company,
          a.account_id as loan_account_id,
          jl.txn_id as journal_txn_id
        from fin.deposit d
        inner join fin.deposit_line dl on
          d.txn_id = dl.txn_id and 
          d.company = dl.company and 
          d.origin = dl.origin
        inner join fin.account a on a.account_id = dl.account_id and a.company = dl.company and a.origin = dl.origin 
        left join fin.txn t on t.txn_id = d.txn_id and t.company = d.company and t.origin = d.origin 
        inner join fin.journalentry_line jl on a.account_id = jl.account_id and a.company = jl.company 
        where a.account_subtype = 'LoansToOthers' and t.created_at >= $1 and d.company = $2 and jl."entry_type" = 'debit' and d.origin = $3
        group by 1, 2, 3, 4
      ),
      loan_account_linked_payments as (
        select
          pl.txn_id as payment_txn_id,
          pl.company,
          pl.origin,
          da.journal_txn_id
        from deposit_linked_loan_accounts da
        left join fin.payment_line pl on da.journal_txn_id = pl.linked_id and da.company = pl.company and da.origin = pl.origin 
        where pl.linked_type = 'journalentry'
      ),
      loan_account_linked_invoices as (
        select
          pl.origin,
          pl.company,
          pl.linked_id as invoice_txn_id
        from loan_account_linked_payments lp
        left join fin.payment_line pl on lp.payment_txn_id = pl.txn_id and lp.company = pl.company and lp.origin = pl.origin
        where pl.linked_type = 'invoice'
        group by 1, 2, 3
      ),
      all_invoices as (
        select
          invoice_txn_id as txn_id,
          origin,
          company,
          'invoice'::fin.txn_type as txn_type
        from loan_account_linked_invoices

        union all

        select
          invoice_txn_id as txn_id,
          origin,
          company,
         'invoice'::fin.txn_type as txn_type
        from payment_linked_invoices
      )
      select 
        ai.txn_id
      from all_invoices ai
      left join fin.electronic_invoice ei on
        ei.txn_id = ai.txn_id and
        ei.company = ai.company and
        ei.origin = ai.origin and
        ei.txn_type = ai.txn_type
      -- Do not return invoices that have been transmitted / recorded on table in Final state of PAID
      where ei.id is null or lower(ei.payment_status) != 'paid'
    `, [startDate, companyCode, origin])

    return {
      origin,
      companyCode,
      eventType: 'invoice-payment',
      transactions: rows.map(r => ({ id: r.txn_id }))
    }
  }

  async getTransactionsWithVoidedEvents (origin, companyCode, startDate) {
    const { rows } = await this.pgConnection.query(`
      with voided_transactions as (
        select
          i.txn_id,
          i.company,
          i.origin,
          i.txn_type
        from fin.invoice i
        left join fin.txn t on i.txn_id = t.txn_id and i.origin = t.origin and i.company = t.company
        where i.amount = 0 and i.private_note = 'Voided' and t.updated_at >= $1 and i.company = $2 and i.origin = $3

        union all

        select
          c.txn_id,
          c.company,
          c.origin,
          c.txn_type
        from fin.creditmemo c
        left join fin.txn t on c.txn_id = t.txn_id and c.origin = t.origin and c.company = t.company
        where c.amount = 0 and c.private_note = 'Voided' and t.updated_at >= $1 and c.company = $2 and c.origin = $3
      )
      select
        vt.txn_id,
        vt.company,
        vt.origin,
        vt.txn_type,
        ei.invoice_irn
      from voided_transactions vt
      -- Fetch already transmitted transactions that have been voided later on in QBO for reversal transmission, IRN is needed
      inner join fin.electronic_invoice ei on ei.txn_id = vt.txn_id and ei.company = vt.company and ei.origin = vt.origin and ei."txn_type" = vt."txn_type"
      group by 1, 2, 3, 4, 5
    `, [startDate, companyCode, origin])

    return {
      origin,
      companyCode,
      eventType: 'voided',
      transactions: rows.map(r => ({ id: r.txn_id, txnType: r.txn_type, invoiceIrn: r.invoice_irn }))
    }
  }

  async getCreditMemoEvents (origin, companyCode, startDate) {
    const { rows } = await this.pgConnection.query(`
      with creditmemos as (
        -- Fully Applied non-voided credit memos are what get picked for transmission
        select 
          *
        from fin.creditmemo c
        where c.amount > 0 and c.unapplied_amount = 0 and c.txn_date >= $1 and c.company = $2 and c.origin = $3
      ),
      linked_creditmemo_payments as (
        select 
          c.txn_id as creditmemo_txn_id,
          pl.txn_id as payment_txn_id,
          pl.company,
          pl.origin
        from creditmemos c
        left join fin.payment_line pl on c.txn_id = pl.linked_id and c.company = pl.company  and c.origin = pl.origin 
      ),
      linked_creditmemo_invoices as (
        select
          lp.creditmemo_txn_id,
          pl.linked_id as invoice_txn_id,
          lp.origin,
          lp.company 
        from linked_creditmemo_payments lp
        left join fin.payment_line pl on pl.txn_id = lp.payment_txn_id and pl.company = lp.company and pl.origin = lp.origin 
        where pl.linked_type = 'invoice'
      ),
      transmitted_invoices as (
        select
          li.creditmemo_txn_id,
          li.origin,
          li.company,
          li.invoice_txn_id,
          ei.invoice_irn,
          ei.invoice_number,
          ei.issue_date
        from linked_creditmemo_invoices li
        -- Fetch only credit memos with a linked invoice IRN this is needed to send credit note with reference invoices it applies to
        inner join fin.electronic_invoice ei on ei.txn_id = li.invoice_txn_id and ei.company = li.company and ei.origin = li.origin
      ),
      diff_lines as (
        -- Filter out credit memo lines that have been recorded as transmitted on the fin.electronic_invoice table
        select
          ti.*
        from transmitted_invoices ti
        left join fin.electronic_invoice ei on 
          ti.creditmemo_txn_id = ei.txn_id and 
          ti.company = ei.company and 
          ti.origin = ei.origin and 
          ei.txn_type = 'creditmemo'
        where ei.id is null
      )
      select
        creditmemo_txn_id as txn_id,
        origin,
        company,
        coalesce(
          jsonb_agg(
            distinct jsonb_build_object(
              'invoiceIrn', invoice_irn,
              'issueDate', issue_date
            )
          ) filter (where invoice_irn is not null),
        '[]'::jsonb
        ) as invoices
      from diff_lines
      group by 1, 2, 3
    `, [startDate, companyCode, origin])

    return {
      origin,
      companyCode,
      eventType: 'creditmemo',
      transactions: rows.map(r => ({ id: r.txn_id, invoiceIrns: r.invoices }))
    }
  }
}

module.exports = ElectronicInvoicingEventEmitter
